import { createContext, useContext, ReactNode, useCallback } from "react"
import { DashboardUser } from "@hydrologiq/common/providers/FirebaseAuth"

// Types for REST API
interface RestApiOptions extends RequestInit {
  params?: Record<string, string | boolean | number | undefined | null>
}

interface RestApiContextType {
  get: (endpoint: string, options?: RestApiOptions) => Promise<Response>
  post: (endpoint: string, data?: any, options?: RestApiOptions) => Promise<Response>
  put: (endpoint: string, data?: any, options?: RestApiOptions) => Promise<Response>
  patch: (endpoint: string, data?: any, options?: RestApiOptions) => Promise<Response>
  delete: (endpoint: string, options?: RestApiOptions) => Promise<Response>
  download: (endpoint: string, options?: RestApiOptions) => Promise<void>
  restEndpoint: string
}

// Create context
const RestApiContext = createContext<RestApiContextType | null>(null)

// Hook to use the REST API
export const useRestApi = () => {
  const context = useContext(RestApiContext)
  if (!context) {
    throw new Error("useRestApi must be used within RestApiProvider")
  }
  return context
}

// Provider component
export function RestApiProvider({
  children,
  user,
  restEndpoint,
  token,
  authType,
}: {
  children: ReactNode
  user?: DashboardUser
  restEndpoint: string
  token?: string
  authType?: "firebase" | "better-auth"
}) {
  console.log("RestApiProvider rendered with:", { user: !!user, token: !!token, authType, restEndpoint })
  // Helper function to get auth headers
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    try {
      let authToken = ""

      // Priority 1: For better-auth, use the bearer token from localStorage
      // The session token (passed as `token` prop) is a session ID, not a JWT.
      // The actual API bearer token is stored in localStorage by the auth client's
      // onSuccess handler when it receives the `set-auth-token` response header.
      if (authType === "better-auth") {
        const betterAuthToken = localStorage.getItem("bearer_token")
        if (betterAuthToken) {
          authToken = betterAuthToken
        } else if (token) {
          // Fallback to session token if bearer_token not yet stored
          authToken = token
        }
      }
      // Priority 2: Direct token provided (non-better-auth)
      else if (token) {
        authToken = token
      }
      // Priority 3: Firebase user provided (legacy) - only if user has firebaseUser
      else if (user?.firebaseUser && typeof user.firebaseUser.getIdTokenResult === "function") {
        const idTokenResult = await user.firebaseUser.getIdTokenResult()
        authToken = idTokenResult.token
      }
      // Priority 4: Fallback to localStorage better-auth token
      else {
        const betterAuthToken = localStorage.getItem("bearer_token")
        if (betterAuthToken) {
          authToken = betterAuthToken
        }
      }

      if (!authToken) {
        throw new Error("No authentication token available")
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      }

      // Include x-auth-type header so the backend knows how to validate the token
      if (authType === "better-auth") {
        headers["x-auth-type"] = "better-auth"
      }

      return headers
    } catch (error) {
      console.error("Failed to get auth token:", error)
      throw new Error("Authentication failed")
    }
  }, [user, token, authType])

  // Helper function to build URL with query parameters
  const buildUrl = useCallback(
    (endpoint: string, params?: Record<string, string | boolean | number | undefined | null>): string => {
      const url = new URL(restEndpoint + endpoint)

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            url.searchParams.append(key, String(value))
          }
        })
      }

      return url.toString()
    },
    [restEndpoint],
  )

  // Generic request function
  const makeRequest = useCallback(
    async (endpoint: string, method: string, data?: any, options: RestApiOptions = {}): Promise<Response> => {
      const { params, headers: customHeaders, ...fetchOptions } = options

      try {
        const authHeaders = await getAuthHeaders()
        const url = buildUrl(endpoint, params)

        const requestOptions: RequestInit = {
          method,
          headers: {
            ...authHeaders,
            ...customHeaders,
          },
          ...fetchOptions,
        }

        // Add body for POST/PUT/PATCH requests
        if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
          requestOptions.body = JSON.stringify(data)
        }

        const response = await fetch(url, requestOptions)

        if (!response.ok) {
          throw new Error(`${method} ${endpoint} failed: ${response.status} ${response.statusText}`)
        }

        return response
      } catch (error) {
        console.error(`REST API ${method} ${endpoint} error:`, error)
        throw error
      }
    },
    [getAuthHeaders, buildUrl],
  )

  // HTTP method functions
  const get = useCallback(
    (endpoint: string, options?: RestApiOptions) => makeRequest(endpoint, "GET", undefined, options),
    [makeRequest],
  )

  const post = useCallback(
    (endpoint: string, data?: any, options?: RestApiOptions) => makeRequest(endpoint, "POST", data, options),
    [makeRequest],
  )

  const put = useCallback(
    (endpoint: string, data?: any, options?: RestApiOptions) => makeRequest(endpoint, "PUT", data, options),
    [makeRequest],
  )

  const patch = useCallback(
    (endpoint: string, data?: any, options?: RestApiOptions) => makeRequest(endpoint, "PATCH", data, options),
    [makeRequest],
  )

  const deleteMethod = useCallback(
    (endpoint: string, options?: RestApiOptions) => makeRequest(endpoint, "DELETE", undefined, options),
    [makeRequest],
  )

  // Special download function that handles file downloads
  const download = useCallback(
    async (endpoint: string, options: RestApiOptions = {}) => {
      try {
        const response = await get(endpoint, options)

        // Extract filename from Content-Disposition header or use default
        let filename = typeof options.params?.filename === "string" ? options.params.filename : "download"
        const contentDisposition = response.headers.get("content-disposition")
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=([^;]+)/)
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/['"]/g, "")
          }
        }
        console.log("filename", filename)

        // Create blob and trigger download
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error("Download failed:", error)
        throw error
      }
    },
    [get],
  )

  const value: RestApiContextType = {
    get,
    post,
    put,
    patch,
    delete: deleteMethod,
    download,
    restEndpoint,
  }

  return <RestApiContext.Provider value={value}>{children}</RestApiContext.Provider>
}
